import React, { useEffect, useMemo } from "react";
import Page from "components/Page";
import { Col, Row } from "reactstrap";
import { decodeId, setStorage } from "utils";
import { setOfficeNavigationContext } from "utils/officeNavigationContext";
import useSubscriptionAccess from "hooks/useSubscriptionAccess";
import { useOfficeDetail } from "repositories/office-repository";
import { withTranslation } from "react-i18next";
import styles from "./OfficeOptions.module.scss";
import timesheetIcon from "assets/images/timesheet-icon.svg";
import noticesIcon from "assets/images/notices.svg";
import constants from "./../../../constants";
import notesIcon from "assets/images/notes.svg";
import preferenceIcon from "assets/images/preferences.svg";
import contractsIcon from "assets/images/contracts.svg";
import staffMembersIcon from "assets/images/staff-members.svg";
import performanceReviewIcon from "assets/images/performance_review.svg";
import waitingListIcon from "assets/images/waiting_list.svg";
import leavesIcon from "assets/images/leaves-icon.svg";
import DashboardIcon from "assets/images/Dashboard.svg";
import PatientsIcon from "assets/images/patients.svg";
import patientAppointmentIcon from "assets/images/patient_apppointment.svg";
import MessageCenter from "assets/images/patient_message_center.svg";
import timesheetReportIcon from "assets/images/timesheetReport.svg";
import Card from "./Card";
import SchedulerIcon from "assets/images/scheduler_new_icon.svg";
import TeamDashboardIcon from "assets/images/team_dashboard.svg";
import TaskIcon from "assets/images/task_management_card.svg";
import NotesIcon from "assets/images/notes_new.svg";
import PatientIcon from "assets/images/patient_chat_new.svg";
import PolicyIcon from "assets/images/policy.svg";
import Info_book from "assets/images/info_book.svg";
import UpdateSubscriptionModel from "components/UpdateSubscriptionModel";
import { useSelector } from "react-redux";
import { initialSubscriptionPagesStateValues } from "reducers/Subscription";
function OfficeOptions({ history, location, match, t }) {
  const subscriptionDetails = useSelector(
    (prev) => prev.Subscription.planfeatureAccessForPages
  );
  const {
    isNoticiesFeatureEnabled,
    isTaskFeatureEnabled,
    isNotesAndReminderFeatureEnabled,
    isPerformanceReviewFeatureEnabled,
    isDiscussionNotesFeatureEnabled,
    isPatientAppointmentFeatureEnabled,
    isPatientConversationFeatureEnabled,
    isSchedulerFeatureEnabled,
    isTeamConversationFeatureEnabled,
    subscriptionPlanId,
  } = useMemo(() => {
    if (subscriptionDetails) {
      return subscriptionDetails;
    }
    return initialSubscriptionPagesStateValues;
  }, [subscriptionDetails]);

  const { isModuleDisabledClass, setIsSubscriptionModel } =
    useSubscriptionAccess();
  const goBack = () => history.push(constants.routes.accountOwner.offices);
  let officeName = null;
  if (location?.state?.officeName) {
    officeName = location.state.officeName;
  }

  const { isLoading, data } = useOfficeDetail(decodeId(match.params.officeId));

  const handleRedirect = (officeId) => {
    history.push({
      pathname: constants.routes.appointment.appointmentScreen,
      state: {
        officeId,
        activetabs: 1,
        statusFilter: [],
        isfromPaitentDetail: true,
      },
    });
  };

  const handlePatientRedirect = (officeId) => {
    history.push(
      constants.routes.appointment.patients.replace(":officeId", officeId)
    );
    setStorage("officeState", {
      officeState: {},
    });
  };

  useEffect(() => {
    setStorage("isRecallReminderPlan", {
      isRecallReminderPlan: false,
    });
    setStorage(constants.notes.cache.isChatFromDashBoard, false);
    setStorage("is-from-appointment-dashboard", false);
    setStorage("isFromPatientAppointment", false);
    setStorage("isFromPatientChat", false);
  }, []);

  useEffect(() => {
    setOfficeNavigationContext({
      officeSlection: true,
      staffOfficeSlection: false,
      isAdminControl: false,
      officeId: data?.id,
      officeName: data?.name,
      ownerId: data?.ownerId ?? null,
    });
  }, [data]);

  return (
    <Page
      onBack={goBack}
      isTitleLoading={!officeName && isLoading}
      title={officeName || (data && data.name)}
      className={styles["page"]}
    >
      <>
        {/* Office Management */}
        <div>
          <p className={`${styles["mobile-text-size"]}  ${styles["account-preference-title-margin"]}`}> 
            {t("officeMangementCategory")}
          </p>
          <Row>
            <Col lg="4">
              <Card
                to={constants.routes.accountOwner.staffGrid.replace(
                  ":officeId",
                  match.params.officeId
                )}
                icon={staffMembersIcon}
                title={t("accountOwner.staffMembers")}
                officeName={officeName}
              />
            </Col>
            <Col
              lg="4"
              onClick={() => {
                setStorage("IsFromLeaveSchedule", {
                  isFromleaveConflict: false,
                });
              }}
            >
              <Card
                to={constants.routes.scheduler.calendar}
                icon={SchedulerIcon}
                title={t("navbar.scheduler")}
                officeName={officeName}
                isDisabled={!isSchedulerFeatureEnabled}
                setIsSubscriptionModel={setIsSubscriptionModel}
              />
            </Col>
            <Col lg="4">
              <Card
                to={constants.routes.dashboard.managerDashboard}
                icon={TeamDashboardIcon}
                title={t("dashBoard.teamDashboard")}
                officeName={officeName}
              />
            </Col>
            <Col lg="4">
              <Card
                to={constants.routes.accountOwner.timelinePreferences.replace(
                  ":officeId",
                  match.params.officeId
                )}
                icon={preferenceIcon}
                title={t("accountOwner.preferences")}
                officeName={officeName}
              />
            </Col>
            <Col lg="4">
              <Card
                to={
                  constants.routes.officePolicyManagements
                    .officePolicyManagement
                }
                icon={PolicyIcon}
                title={t("navbar.policyManagement")}
                isDisabled={!isTaskFeatureEnabled}
              />
            </Col>
            <Col lg="4">
              <Card
                to={constants.routes.accountOwner.officeStaffInformation.replace(
                  ":officeId",
                  match.params.officeId
                )}
                locationState={{
                  ...location.state,
                  address: data?.address,
                  contactNumber: data?.contactNumber,
                }}
                icon={Info_book}
                title={t("accountOwner.officeStaffInformation")}
              />
            </Col>
          </Row>
        </div>

        {/* Staff Management */}
        <div>
          <p className={styles["mobile-text-size"] + " " + "mt-4"}>
            {t("staffManagementCategory")}
          </p>
          <Row>
            <Col lg="4">
              <Card
                to={constants.routes.accountOwner.timesheet.replace(
                  ":officeId",
                  match.params.officeId
                )}
                icon={timesheetIcon}
                title={t("contracts.timesheet")}
              />
            </Col>
            <Col lg="4">
              <Card
                to={constants.routes.accountOwner.leaves.replace(
                  ":officeId",
                  match.params.officeId
                )}
                icon={leavesIcon}
                title={t("contracts.leaves")}
              />
            </Col>
            <Col lg="4">
              <Card
                to={constants.routes.accountOwner.timesheetReport.replace(
                  ":officeId",
                  match.params.officeId
                )}
                icon={timesheetReportIcon}
                title={t("staffTimesheet.timesheetReport")}
              />
            </Col>
            <Col lg="4">
              <Card
                to={constants.routes.chatModule}
                icon={MessageCenter}
                title={t("messenger.teamConversation")}
                isDisabled={!isTeamConversationFeatureEnabled}
                setIsSubscriptionModel={setIsSubscriptionModel}
              />
            </Col>
            <Col lg="4">
              <Card
                to={constants.routes.taskManagements.taskManagement}
                icon={TaskIcon}
                title={t("navbar.taskManagement")}
                isDisabled={!isTaskFeatureEnabled}
                setIsSubscriptionModel={setIsSubscriptionModel}
              />
            </Col>
            <Col lg="4">
              <Card
                to={constants.routes.notes}
                icon={NotesIcon}
                title={t("Notes & Reminder")}
                isDisabled={!isNotesAndReminderFeatureEnabled}
                setIsSubscriptionModel={setIsSubscriptionModel}
              />
            </Col>
            <Col lg="4">
              <Card
                to={constants.routes.accountOwner.performanceReview.replace(
                  ":officeId",
                  match.params.officeId
                )}
                icon={performanceReviewIcon}
                title={t("performanceReview.performanceReview")}
                officeName={officeName}
                designation={location?.state?.designation}
                isDisabled={!isPerformanceReviewFeatureEnabled}
                setIsSubscriptionModel={setIsSubscriptionModel}
              />
            </Col>
            <Col lg="4">
              <Card
                to={constants.routes.accountOwner.notices.replace(
                  ":officeId",
                  match.params.officeId
                )}
                icon={noticesIcon}
                title={t("notices.notice")}
                officeName={officeName}
                isDisabled={!isNoticiesFeatureEnabled}
                setIsSubscriptionModel={setIsSubscriptionModel}
              />
            </Col>
            <Col lg="4">
              <Card
                to={constants.routes.accountOwner.discussionNotes.replace(
                  ":officeId",
                  match.params.officeId
                )}
                icon={notesIcon}
                title={t("discussionNote.discussionNote")}
                officeName={officeName}
                isDisabled={!isDiscussionNotesFeatureEnabled}
                setIsSubscriptionModel={setIsSubscriptionModel}
              />
            </Col>
            <Col
              lg="4"
              className={isModuleDisabledClass(
                constants.moduleNameWithId.formAndContracts
              )}
            >
              <Card
                to={constants.routes.accountOwner.forms.replace(
                  ":officeId",
                  match.params.officeId
                )}
                icon={contractsIcon}
                title={t("contracts.formsAndContract")}
                officeName={officeName}
              />
            </Col>
          </Row>
        </div>

        {/* Patient Management */}
        <div>
          <p className={styles["mobile-text-size"] + " " + "mt-4"}>
            {t("pateintManagementCategory")}
          </p>
          <Row>
            <Col lg="4">
              <Card
                redirectTo={() => handlePatientRedirect(match.params.officeId)}
                icon={PatientsIcon}
                title={t("patients.patients")}
              />
            </Col>
            <Col lg="4">
              <Card
                redirectTo={() =>
                  handleRedirect(decodeId(match.params.officeId))
                }
                icon={patientAppointmentIcon}
                title="Patient Appointments"
                isDisabled={!isPatientAppointmentFeatureEnabled}
                setIsSubscriptionModel={setIsSubscriptionModel}
              />
            </Col>
            <Col lg="4">
              <Card
                to={constants.routes.waitingList.replace(
                  ":officeId",
                  match.params.officeId
                )}
                icon={waitingListIcon}
                title={t("waitingList.waitingList")}
              />
            </Col>
            <Col lg="4">
              <Card
                to={constants.routes.superAdmin.appointmentDashboard.replace(
                  ":officeId",
                  match.params.officeId
                )}
                icon={DashboardIcon}
                title="Patient Dashboard"
              />
            </Col>
            <Col lg="4">
              <Card
                to={constants.routes.appointment.patientMessageCenter
                  .replace(":officeId", match.params.officeId)
                  .replace(
                    "/:patientId?",
                    match?.params?.patientId
                      ? `/${match?.params?.patientId}`
                      : ""
                  )
                  .replace(
                    "/:appointmentId?",
                    match?.params?.appointmentId
                      ? `/${match?.params?.appointmentId}`
                      : ""
                  )}
                icon={MessageCenter}
                title="Patient Message Center"
              />
            </Col>
            <Col lg="4">
              <Card
                to={constants.routes.patientChatModule}
                icon={PatientIcon}
                title={t("messenger.patientLiveChat")}
                isDisabled={!isPatientConversationFeatureEnabled}
                setIsSubscriptionModel={setIsSubscriptionModel}
              />
            </Col>
          </Row>
        </div>
        <UpdateSubscriptionModel
          t={t}
          isFromOfficeOptions={true}
          plan={subscriptionPlanId}
        />
      </>
    </Page>
  );
}

export default withTranslation()(OfficeOptions);
